import React, { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { toast } from "sonner";
import axios from "axios";
import { Search, CheckSquare, Square } from "lucide-react";

/**
 * Props:
 * - isOpen, onClose
 * - drivers: full list from table (array of driver objects)
 * - baseurl, token, setIsUnauthorised
 * - defaultTeam / defaultStatus (optional: to pre-filter)
 *
 * API:
 * Sends POST to `${baseurl}/api/User/v1.0/sendNotification`
 * Payload shape:
 * {
 *   driverIds: [ "DRV123", "DRV456", ... ],
 *   title: string,
 *   body: string,
 *   image: "",
 *   extra: ""
 * }
 * If your backend expects a different key (e.g. `driverId` as array),
 * change `driverIds` below accordingly.
 */
export default function BulkSendNotificationModal({
  isOpen,
  onClose,
  drivers = [],
  baseurl,
  token,
  setIsUnauthorised,
  defaultTeam = "",
  defaultStatus = ""
  , AdminId
}) {
  // ---- Preconfigured messages (same spirit as single-send)
  const preConfiguredMessages = [
    { title: "Shift Reminder", body: "Your shift starts in 30 minutes. Please be ready." },
    { title: "Update Location", body: "Please update your live location in the app." },
    { title: "Urgent Task", body: "You have been assigned a new urgent task. Check now." },
    { title: "Custom", body: "" },
  ];

  // ---- UI state
  const [query, setQuery] = useState("");
  const [team, setTeam] = useState(defaultTeam);
  const [status, setStatus] = useState(defaultStatus);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // message
  const [selectedMessageIndex, setSelectedMessageIndex] = useState(0);
  const [customTitle, setCustomTitle] = useState("");
  const [customBody, setCustomBody] = useState("");
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  // ---- Derived list (search + filters)
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (drivers || []).filter(d => {
      const matchesQ =
        !q ||
        d.fullName?.toLowerCase().includes(q) ||
        d.username?.toLowerCase().includes(q) ||
        String(d.driverId || "").toLowerCase().includes(q);
      const matchesTeam = team ? d.teamName === team : true;
      const matchesStatus = status ? d.status === status : true;
      return matchesQ && matchesTeam && matchesStatus;
    });
  }, [drivers, query, team, status]);

  // ---- Select all toggling for current filtered view
  useEffect(() => {
    if (!isOpen) return;
    if (selectAll) {
      const newSet = new Set(selectedIds);
      filtered.forEach(d => newSet.add(d.driverId));
      setSelectedIds(newSet);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectAll, filtered]);

  const isCustom = preConfiguredMessages[selectedMessageIndex].title === "Custom";

  const toggleOne = (driverId) => {
    const next = new Set(selectedIds);
    if (next.has(driverId)) next.delete(driverId);
    else next.add(driverId);
    setSelectedIds(next);
  };

  const toggleSelectAllCurrentPage = () => {
    // If all filtered are already selected, unselect them; else select them.
    const allSelected = filtered.every(d => selectedIds.has(d.driverId));
    const next = new Set(selectedIds);
    if (allSelected) {
      filtered.forEach(d => next.delete(d.driverId));
      setSelectAll(false);
    } else {
      filtered.forEach(d => next.add(d.driverId));
      setSelectAll(true);
    }
    setSelectedIds(next);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectAll(false);
  };

  const handleSend = async () => {
    const finalTitle = isCustom ? customTitle : preConfiguredMessages[selectedMessageIndex].title;
    const finalBody = isCustom ? customBody : preConfiguredMessages[selectedMessageIndex].body;

    const newErrors = {};
    if (!finalTitle.trim()) newErrors.title = "Title is required";
    if (!finalBody.trim()) newErrors.body = "Body is required";
    if (selectedIds.size === 0) newErrors.recipients = "Pick at least one driver";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setSending(true);
    try {
      const payload = {

        driverId: Array.from(selectedIds), // ⬅️ array of IDs
        title: finalTitle,
        body: finalBody,
        image: "",
        extra: "",
        channelId: "", adminId: AdminId
      };

      const response = await axios.post(
        `${baseurl}/api/User/v1.0/sendNotification`,
        payload,
        { headers: { Authorization: `Basic ${token}` } }
      );
      console.log(response)

      if (response.data.responseCode === "00") {
        toast.success(`Notification sent to ${payload.driverId.length} driver(s).`);
        // reset relevant state
        setCustomTitle("");
        setCustomBody("");
        clearSelection();
        onClose();
      } else if (response.data.responseCode === "401") {
        setIsUnauthorised?.(true);
      } else {
        toast.error(response.data.responseMessage || "Failed to send notification");
      }
    } catch (err) {
      toast.error("Error sending notifications");
    } finally {
      setSending(false);
    }
  };

  // ---- Industrial modern look via utility classes
  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-50" />
        <Dialog.Content className="bg-white rounded-2xl shadow-xl p-0 w-[95%] max-w-3xl fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-5 py-3 border-b bg-[#0E253C] text-white flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold tracking-wide">
              Send Bulk Notification
            </Dialog.Title>
            <Dialog.Close className="text-white/80 hover:text-white text-sm">Close</Dialog.Close>
          </div>

          {/* Body */}
          <div className="p-5 grid grid-cols-1 md:grid-cols-5 gap-5">
            {/* Left: recipients */}
            <div className="md:col-span-3">
              <div className="flex items-center gap-2 mb-3">
                <div className="relative flex-1">
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm shadow-sm"
                    placeholder="Search by name, username, or Driver ID"
                  />
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                </div>

                <select
                  value={team}
                  onChange={(e) => setTeam(e.target.value)}
                  className="border rounded-lg px-2 py-2 text-sm shadow-sm"
                >
                  <option value="">All Teams</option>
                  {/* If you want dynamic teams, pass them as prop or read from context where used */}
                </select>

                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="border rounded-lg px-2 py-2 text-sm shadow-sm"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="offline">Offline</option>
                </select>
              </div>

              <div className="flex items-center justify-between mb-2">
                <button
                  type="button"
                  onClick={toggleSelectAllCurrentPage}
                  className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 border"
                >
                  {filtered.length > 0 && filtered.every(d => selectedIds.has(d.driverId))
                    ? "Unselect All (filtered)"
                    : "Select All (filtered)"}
                </button>

                <div className="text-xs text-gray-600">
                  Selected: <span className="font-semibold">{selectedIds.size}</span> / {drivers.length}
                </div>
              </div>

              <div className="h-[320px] overflow-auto rounded-lg border">
                <table className="min-w-full text-sm">
                  <thead className="bg-[#EDEAE5] text-gray-700 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 w-10 text-center">#</th>
                      <th className="px-3 py-2 text-left">Driver</th>
                      <th className="px-3 py-2 text-left">Username</th>
                      <th className="px-3 py-2 text-center">ID</th>
                      <th className="px-3 py-2 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-3 py-10 text-center text-gray-500">
                          No drivers found for current filters.
                        </td>
                      </tr>
                    ) : filtered.map((d, idx) => {
                      const checked = selectedIds.has(d.driverId);
                      return (
                        <tr key={d.driverId} className="border-t hover:bg-gray-50">
                          <td className="px-3 py-2 text-center">
                            <button
                              type="button"
                              className="inline-flex items-center justify-center"
                              onClick={() => toggleOne(d.driverId)}
                              title={checked ? "Unselect" : "Select"}
                            >
                              {checked ? <CheckSquare size={16} /> : <Square size={16} />}
                            </button>
                          </td>
                          <td className="px-3 py-2">{d.fullName}</td>
                          <td className="px-3 py-2">{d.username}</td>
                          <td className="px-3 py-2 text-center">{d.driverId}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${d.status === "active"
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                              }`}>
                              {d.status?.[0]?.toUpperCase() + d.status?.slice(1)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {errors.recipients && (
                <p className="text-red-500 text-xs mt-2">{errors.recipients}</p>
              )}
            </div>

            {/* Right: message composer */}
            <div className="md:col-span-2">
              <div className="rounded-xl border p-3 bg-white shadow-sm">
                <label className="block text-sm font-medium mb-1">Choose Message</label>
                <select
                  value={selectedMessageIndex}
                  onChange={(e) => setSelectedMessageIndex(Number(e.target.value))}
                  className="border rounded-lg px-3 py-2 w-full mb-3 text-sm shadow-sm"
                >
                  {preConfiguredMessages.map((msg, idx) => (
                    <option key={idx} value={idx}>{msg.title}</option>
                  ))}
                </select>

                {isCustom && (
                  <>
                    <div className="mb-3">
                      <label className="block text-sm font-medium mb-1">Title</label>
                      <input
                        type="text"
                        value={customTitle}
                        onChange={(e) => setCustomTitle(e.target.value)}
                        className={`border rounded-lg px-3 py-2 w-full text-sm shadow-sm ${errors.title ? "border-red-500" : ""}`}
                        placeholder="Enter custom title"
                      />
                      {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
                    </div>

                    <div className="mb-2">
                      <label className="block text-sm font-medium mb-1">Body</label>
                      <textarea
                        rows={5}
                        value={customBody}
                        onChange={(e) => setCustomBody(e.target.value)}
                        className={`border rounded-lg px-3 py-2 w-full text-sm shadow-sm ${errors.body ? "border-red-500" : ""}`}
                        placeholder="Enter custom message body"
                      />
                      {errors.body && <p className="text-red-500 text-xs mt-1">{errors.body}</p>}
                    </div>
                  </>
                )}

                {!isCustom && (
                  <div className="text-xs text-gray-600 bg-gray-50 border rounded-lg p-3">
                    <div className="font-semibold mb-1">
                      Preview
                    </div>
                    <div className="mb-1">
                      <span className="font-medium">Title:</span> {preConfiguredMessages[selectedMessageIndex].title}
                    </div>
                    <div>
                      <span className="font-medium">Body:</span> {preConfiguredMessages[selectedMessageIndex].body}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t bg-[#EDEAE5] flex items-center justify-between">
            <button
              type="button"
              onClick={clearSelection}
              className="text-xs px-3 py-1.5 rounded bg-white hover:bg-gray-100 border shadow-sm"
            >
              Clear Selection
            </button>

            <div className="flex items-center gap-2">
              <Dialog.Close asChild>
                <button className="px-3 py-1.5 rounded bg-white hover:bg-gray-100 border text-sm shadow-sm">
                  Cancel
                </button>
              </Dialog.Close>

              <button
                onClick={handleSend}
                disabled={sending}
                className="px-4 py-2 rounded-lg bg-[#FC981B] hover:bg-[#e58612] text-white text-sm shadow disabled:opacity-60"
                title={selectedIds.size === 0 ? "Select at least one driver" : "Send"}
              >
                {sending ? "Sending..." : `Send to ${selectedIds.size || 0} driver(s)`}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
